/**
 * Мост до сервера.
 *
 * Наружу send отправляет действие, а подписка на state отдаёт единый объект,
 * который компоненты только читают.
 *
 * Идентичность игрока в send не передаётся: сервер берёт её из своей сессии.
 * Подробнее и о расхождении с исходной формулировкой задания — в README.md.
 */

import { useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import {
  CHANNEL_ACTION,
  CHANNEL_EVENT,
  type ClientAction,
  type Command,
  type CommandResult,
  RejectCode,
  type ServerMessage,
  type HarnessState,
  type RoomInfo,
  applyRooms,
  scrubMessage,
  type Snapshot,
} from '../srv/protocol.js'

/**
 * Попытки вернуть связь после обрыва: ждём 5, 15 и 30 секунд перед первой, второй и третьей.
 * Отсчёт виден игроку. Если и третья не удалась, клиент сдаётся и ждёт «Retry»: тот пробует
 * сразу, а затем тот же ряд. Партия переживает перезапуск сервера, но вечный повтор писал бы
 * «Reconnecting…», ничего не меняя.
 */
const RETRY_DELAYS_MS = [5_000, 15_000, 30_000]

/** Команда без `commandId`: его дописывает мост. */
type CommandBody = Command extends infer C ? (C extends Command ? Omit<C, 'commandId'> : never) : never

/** Сколько ждать ответа сервера на действие, мс. */
const ACK_TIMEOUT_MS = 8_000
const NO_REPLY: CommandResult = {
  ok: false,
  code: RejectCode.BAD_ACTION,
  reason: 'No answer from the server',
}

/** Короткая форма идентификатора: в узкой колонке журнала полный не помещается. */
const short = (id: string | undefined): string => (id ? id.slice(0, 6) : '?')

interface LogLine {
  id: number
  iso: string
  channel: 'net' | 'beat' | 'ping' | 'game' | 'room'
  text: string
  /** Комната для фильтра журнала; null — событие соединения вне комнаты. */
  roomCode: string | null
  /** Сколько раз подряд пришла такая же строка. Единица не показывается. */
  repeat: number
}

/** Роль в комнате: игрок со слотом или зритель без него. */
export type Role = 'player' | 'viewer'

export interface ClientState {
  connected: boolean
  connecting: boolean
  socketId: string | null
  /** Следующая попытка уже назначена: какая по счёту и когда (метка локальных часов). */
  retry: { attempt: number; of: number; at: number } | null
  /** Все попытки не удались: сервер недоступен, клиент ждёт «Retry». */
  unreachable: boolean
  harness: HarnessState | null
  /**
   * Открытые комнаты для лобби. Обновляется сервером при любом изменении.
   * `null` — первый список ещё не пришёл: пустой список значит «комнат нет»,
   * а не «не знаем», и лобби обязано различать эти два случая.
   */
  rooms: RoomInfo[] | null
  /** Последний отказ на вход: показывается в лобби под полем. */
  lastError: string | null
  /** Код отказа: по нему лобби подбирает слова и решает, что открыть. */
  lastErrorCode: string | null
  /** Кто мы в комнате: игрок со слотом или зритель без него, управление недоступно (R112); `null` — вне комнаты. */
  role: Role | null
  /**
   * Идёт возврат в сохранённую партию: вкладку перезагрузили, токен в
   * хранилище есть, ответа сервера ещё нет. Пока это правда, показывать
   * лобби нельзя — партия, скорее всего, вернётся, и лобби мелькнёт зря.
   */
  returning: boolean
  /**
   * Игрок вышел в лобби, не выходя из партии: слот, сессия и снимок остаются, а на экране — лобби.
   * Из него доступна только «своя» комната (вернуться) или выход насовсем.
   */
  lobbyView: boolean
  playerId: string | null
  sessionToken: string | null
  code: string | null
  snapshot: Snapshot | null
  log: LogLine[]
}

type StateListener = (state: ClientState) => void

const MAX_LOG = 200

/*
 * Настройки клиента в `localStorage`. Приватный режим и запрет хранилища не должны ронять клиент:
 * чтение даёт `null`, запись молчит, и выбор живёт до перезагрузки.
 */
export const stored = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export const store = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // См. выше.
  }
}

/** Ключ сессии в sessionStorage. Вкладку перезагрузили — слот в партии не потерян. */
const SESSION_KEY = 'tolk.session'

interface StoredSession {
  code: string
  sessionToken: string
  /** Игрок был в лобби с сохранённым местом: после перезагрузки он остаётся там же. */
  lobby?: boolean
}

function loadSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as StoredSession) : null
  } catch {
    // Приватный режим и запрет хранилища не должны ронять клиент.
    return null
  }
}

function saveSession(session: StoredSession | null): void {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
    else sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // См. loadSession: хранилище может быть недоступно.
  }
}

const restored = loadSession()

/** Каналы, где важно последнее значение, а не история: строки схлопываются. */
const COLLAPSING = new Set<LogLine['channel']>(['beat', 'ping'])

class Bridge {
  private socket: Socket | null = null
  private stateListeners = new Set<StateListener>()
  private logSeq = 0
  private commandSeq = 0
  /** Имя для стенда. Уходит после установки соединения, не раньше. */
  private pendingName: string | null = null
  /** Идёт автоматический возврат по токену: его отказы человеку не показываем. */
  private rejoining = false
  /** Таймер до следующей попытки и сколько попыток из ряда уже сделано. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retryIndex = 0
  /** Имитация сбоя связи (`break()`): попытки вернуть её не удаются, пока не нажат «Retry» или `connect`. */
  private broken = false

  state: ClientState = {
    connected: false,
    connecting: false,
    socketId: null,
    retry: null,
    unreachable: false,
    harness: null,
    rooms: null,
    lastError: null,
    lastErrorCode: null,
    role: null,
    returning: restored !== null,
    lobbyView: restored?.lobby === true,
    playerId: null,
    sessionToken: restored?.sessionToken ?? null,
    code: restored?.code ?? null,
    snapshot: null,
    log: [],
  }

  // --- подписки -------------------------------------------------------------

  subscribe(listener: StateListener): () => void {
    this.stateListeners.add(listener)
    return () => this.stateListeners.delete(listener)
  }

  private patch(next: Partial<ClientState>): void {
    this.state = { ...this.state, ...next }
    for (const listener of this.stateListeners) listener(this.state)
  }

  /**
   * Запись в журнал.
   *
   * Повторяющиеся строки одного канала схлопываются в верхнюю со счётчиком:
   * сердцебиение идёт раз в секунду и иначе вытесняет из журнала всё
   * остальное. Схлопываются только каналы, где важно последнее значение,
   * а не история.
   */
  private write(channel: LogLine['channel'], text: string, roomCode = this.state.code): void {
    const iso = new Date().toISOString()
    const [head, ...rest] = this.state.log
    if (head && COLLAPSING.has(channel) && head.channel === channel && head.roomCode === roomCode) {
      const merged: LogLine = { ...head, iso, text, repeat: head.repeat + 1 }
      this.patch({ log: [merged, ...rest] })
      return
    }
    const line: LogLine = { id: this.logSeq++, iso, channel, text, roomCode, repeat: 1 }
    this.patch({ log: [line, ...this.state.log].slice(0, MAX_LOG) })
  }

  /** Следующий уникальный id команды. Повтор с тем же id сервер не применит дважды. */
  nextCommandId(): string {
    return `c${Date.now().toString(36)}-${this.commandSeq++}`
  }

  /** Команда партии: идентификатор ей выдаёт мост. */
  command(body: CommandBody): void {
    this.send({ kind: 'command', command: { ...body, commandId: this.nextCommandId() } as Command })
  }

  clearLog(roomCode?: string): void {
    this.patch({ log: roomCode ? this.state.log.filter((line) => line.roomCode !== roomCode) : [] })
  }

  // --- соединение -----------------------------------------------------------

  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer)
    this.retryTimer = null
  }

  /**
   * Назначает следующую попытку: через 5, 15, 30 секунд, затем сдаётся. Своя, а не встроенная в
   * Socket.IO: ему нужны видимый игроку отсчёт и остановка после третьей.
   */
  private scheduleRetry(socket: Socket): void {
    if (this.socket !== socket || socket.connected || this.retryTimer !== null) return
    // Ряд задан для игры; сквозные проверки получают от сервера `settings.speed` и ждут во столько же раз меньше.
    const speed = this.state.snapshot?.settings.speed ?? 1
    const wait = RETRY_DELAYS_MS[this.retryIndex]
    if (wait === undefined) {
      this.patch({ connecting: false, retry: null, unreachable: true, returning: false })
      this.write('net', 'gave up reconnecting')
      return
    }
    const delay = wait / speed
    const attempt = this.retryIndex + 1
    this.patch({
      connecting: false,
      retry: { attempt, of: RETRY_DELAYS_MS.length, at: Date.now() + delay },
      returning: false,
    })
    this.write('net', `reconnect ${attempt} of ${RETRY_DELAYS_MS.length} in ${delay / 1000}s`)
    this.retryTimer = globalThis.setTimeout(() => {
      this.retryTimer = null
      if (this.socket !== socket || socket.connected) return
      this.retryIndex = attempt
      this.patch({ connecting: true, retry: null })
      this.write('net', `reconnect ${attempt}`)
      if (!this.broken) return void socket.connect()
      // Сбой имитируется: попытка длится так же, как настоящая, и кончается ошибкой.
      globalThis.setTimeout(() => {
        if (this.socket !== socket) return
        this.write('net', 'connection error: simulated')
        this.patch({ connecting: false })
        this.scheduleRetry(socket)
      }, 400)
    }, delay)
  }

  /** Сразу пробует подключиться; не вышло — дальше ряд 5, 15, 30 секунд. */
  connect(): void {
    this.clearRetry()
    this.retryIndex = 0
    this.broken = false
    // Второй сокет не создаём: StrictMode в разработке монтирует дважды, и без
    // этой ветки открывалось два соединения на одну вкладку. Если прежний
    // экземпляр отвалился, поднимаем его же — иначе после исчерпания попыток
    // кнопка connect не сработала бы вовсе.
    if (this.socket) {
      if (!this.socket.connected) {
        this.patch({ connecting: true, unreachable: false, retry: null })
        this.socket.connect()
      }
      return
    }
    this.patch({ connecting: true, unreachable: false, retry: null })

    const socket = io({
      // long-polling не используем: на функциях и за прокси он лишний.
      transports: ['websocket'],
      // Попытки назначаем сами, см. `scheduleRetry`.
      reconnection: false,
      autoConnect: true,
    })
    this.socket = socket

    socket.on('connect', () => {
      this.clearRetry()
      this.retryIndex = 0
      this.patch({
        connected: true,
        connecting: false,
        socketId: socket.id ?? null,
        retry: null,
        unreachable: false,
      })
      this.write('net', `connected ${short(socket.id)}${socket.recovered ? ' (recovered)' : ''}`)
      // Имя стенду уходит только теперь: до соединения отправлять некуда.
      if (this.pendingName) this.send({ kind: 'harness:hello', name: this.pendingName })
      // После обрыва возвращаемся в свой слот по токену.
      if (this.state.sessionToken && this.state.code) {
        this.rejoining = true
        this.write('net', 'returning with token')
        this.send({
          kind: 'joinGame',
          commandId: this.nextCommandId(),
          code: this.state.code,
          name: 'returning',
          sessionToken: this.state.sessionToken,
        })
      }
    })

    socket.on('disconnect', (reason) => {
      // Свой выход (`disconnect()`) — единственное отключение, после которого связь не ждём.
      this.patch({ connected: false, socketId: null, connecting: false })
      this.write('net', `disconnected: ${reason}`)
      // Любой обрыв, кроме своего, ведёт к ряду попыток; отключение со стороны сервера тоже.
      if (reason !== 'io client disconnect') this.scheduleRetry(socket)
    })

    socket.on('connect_error', (err: Error) => {
      this.write('net', `connection error: ${err.message}`)
      this.scheduleRetry(socket)
    })

    // Ping движка вместо своего сердцебиения: тот же признак живого канала,
    // но без прикладного сообщения на проводе. Метку ставим локально.
    socket.io.on('ping', () => {
      this.write('beat', 'ping')
    })

    socket.on(CHANNEL_EVENT, (message: ServerMessage) => this.receive(scrubMessage(message)))
  }

  /**
   * Имитация обрыва: рвёт транспорт, как пропавшая сеть, и держит попытки неудачными. Идёт весь ряд
   * 5, 15, 30 секунд и ошибка; «Retry» или `connect` снимают сбой и подключают по-настоящему.
   */
  break(): void {
    const socket = this.socket
    if (!socket?.connected) return
    this.broken = true
    this.write('net', 'break: simulated connection failure')
    socket.io.engine.close()
  }

  disconnect(): void {
    this.clearRetry()
    this.broken = false
    this.socket?.disconnect()
    this.socket = null
    this.patch({ connected: false, connecting: false, socketId: null, retry: null })
    this.write('net', 'manual disconnect')
  }

  /** Отправка действия. Обещание закрывается ответом сервера; без связи действие не уходит и ответа нет. */
  send(action: ClientAction): Promise<CommandResult | null> {
    switch (action.kind) {
      case 'createGame':
        this.write('room', `create requested${action.roomName ? `: ${action.roomName}` : ''}`, null)
        break
      case 'joinGame':
        this.write('room', 'join requested', action.code)
        break
      case 'watchGame':
        this.write(
          'room',
          action.code ? 'watch requested' : 'stop watching requested',
          action.code ?? this.state.code,
        )
        break
      case 'deleteRoom':
        this.write('room', 'delete requested', action.code)
        break
      case 'command':
        if (action.command?.type === 'leaveGame') this.write('room', 'leave requested')
        break
    }
    if (!this.socket?.connected) {
      this.write('net', `offline, dropped: ${action.kind}`)
      return Promise.resolve(null)
    }
    // Ответ на действие приходит подтверждением Socket.IO, а не событием. Молчание сервера — отказ.
    return new Promise((resolve) =>
      this.socket!.timeout(ACK_TIMEOUT_MS).emit(
        CHANNEL_ACTION,
        action,
        (error: Error | null, reply?: CommandResult) => {
          const result = !error && reply ? reply : NO_REPLY
          this.onReply(action, result)
          resolve(result)
        },
      ),
    )
  }

  /** Ответ сервера на действие. Принятое видно по событиям комнаты; здесь разбирается только отказ. */
  private onReply(action: ClientAction, reply: CommandResult): void {
    if (reply.ok) return
    this.write('game', `rejected ${reply.code}: ${reply.reason}`)
    // Молчание сервера слот не хоронит: возврат повторится со следующим соединением.
    if (this.rejoining && action.kind === 'joinGame' && reply !== NO_REPLY) {
      // Комнаты больше нет — сервер перезапускался или её убрали по TTL.
      // Сохранённая сессия мертва: гасим её и молча остаёмся в лобби.
      this.rejoining = false
      saveSession(null)
      this.patch({ sessionToken: null, code: null, returning: false })
      return
    }
    /*
     * «Вы не в партии» на экран не выносим. Этот отказ приходит на команду,
     * адресованную партии, из которой мы уже вышли, — человеку в лобби он
     * ничего не объясняет. Вне партии отказ входа (мест нет, цвет занят)
     * человек увидеть обязан; внутри партии отказ команды хода лобби не касается.
     */
    if (reply.code === RejectCode.NOT_JOINED || reply.code === RejectCode.NOT_IN_GAME) return
    if (action.kind === 'command' && this.state.snapshot) return
    this.patch({ lastError: reply.reason, lastErrorCode: reply.code })
  }

  // --- приём ----------------------------------------------------------------

  private receive(message: ServerMessage): void {
    switch (message.kind) {
      case 'rooms': {
        const previous = this.state.rooms
        this.patch({ rooms: applyRooms(previous, message) })
        if (message.full && message.upsert.length === 0) this.write('room', 'room list received: empty', null)
        for (const room of message.upsert) {
          const existed = previous?.some((item) => item.code === room.code) ?? false
          const state = room.started ? 'live' : 'open'
          this.write(
            'room',
            `${message.full ? 'room received' : existed ? 'room updated' : 'room added'}: ${room.roomName} (${room.code}, ${room.players}/${room.maxPlayers}, ${state}, ${room.viewers} viewers)`,
            room.code,
          )
        }
        for (const code of message.remove) this.write('room', `room removed: ${code}`, code)
        return
      }

      case 'harness:state': {
        // Ревизия ушла из панели в журнал: её ценность в том, когда она
        // изменилась, а не в текущем числе.
        const before = this.state.harness?.revision ?? null
        this.patch({ harness: message.state })
        if (before !== message.state.revision) {
          this.write('net', `harness r${message.state.revision}, ${message.state.clients.length} clients`)
        }
        return
      }

      case 'harness:pong': {
        const ms = Date.now() - message.t0
        this.write('ping', `${ms} ms`)
        return
      }

      case 'roomClosed':
        // Комнату удалили, пока мы играли или смотрели: возвращаемся в лобби.
        this.resetGame()
        this.write('game', `room closed: ${message.reason}`)
        break

      case 'game':
        this.applyGameEvent(message)
        break
    }
  }

  private applyGameEvent(message: Extract<ServerMessage, { kind: 'game' }>): void {
    const event = message.event
    // После выхода ещё могут доехать события комнаты, включая снапшот самого
    // выхода. Они не должны вернуть поле или журнал хода в сброшенное состояние.
    if (this.state.role === null && event.type !== 'joined') return
    switch (event.type) {
      case 'joined':
        this.patch({
          role: 'player',
          playerId: event.playerId,
          sessionToken: event.sessionToken,
          snapshot: event.snapshot,
          code: event.snapshot.code,
          lastError: null,
          lastErrorCode: null,
          returning: false,
        })
        this.rejoining = false
        // Пережить перезагрузку вкладки может только то, что записано наружу.
        saveSession({
          code: event.snapshot.code,
          sessionToken: event.sessionToken,
          lobby: this.state.lobbyView,
        })
        this.write(
          'room',
          `joined as ${short(event.playerId)}: ${event.snapshot.roomName} (${event.snapshot.code}, turn ${event.snapshot.turnNumber} · ${event.snapshot.phase.toLowerCase()})`,
          event.snapshot.code,
        )
        break
      case 'snapshot':
        this.patch({ snapshot: event.snapshot, code: event.snapshot.code })
        this.write(
          'room',
          `snapshot r${event.snapshot.revision}: ${event.snapshot.roomName} (${event.snapshot.code}, turn ${event.snapshot.turnNumber}, ${event.snapshot.phase.toLowerCase()})`,
          event.snapshot.code,
        )
        break
      case 'turnStarted':
        this.write('game', `turn ${event.turn} · ${short(event.playerId)} · ${event.phase}`)
        break
      case 'turnResolved':
        this.write('game', `turn ${event.record.turn} · ${event.record.event}`)
        break
      case 'gameFinished':
        saveSession(null)
        this.patch({ sessionToken: null })
        this.write('game', `game finished, winner ${event.winnerId ?? 'none'}`)
        break
    }
  }

  // --- удобные обёртки ------------------------------------------------------

  ping(): void {
    this.send({ kind: 'harness:ping', t0: Date.now() })
  }

  /**
   * Выйти из партии и вернуться в лобби. Локальное состояние гасим сами: сервер
   * снимет нас со своей стороны. Уже отправленные события комнаты могут
   * прийти позже — applyGameEvent отсекает их, пока мы вне партии.
   */
  leaveGame(): void {
    if (this.state.role === 'viewer') this.send({ kind: 'watchGame', code: null })
    else this.command({ type: 'leaveGame' })
    this.resetGame()
  }

  /** Игрок уходит в лобби, сохранив место за столом; зритель выходит как прежде. */
  toLobby(): void {
    if (this.state.role === 'viewer' || !this.state.snapshot) return this.leaveGame()
    this.patch({ lobbyView: true })
    this.persistLobby()
    this.write('room', 'back to lobby (seat kept)')
  }

  /** Вернуться из лобби к столу, за которым держим место. */
  returnToGame(): void {
    this.patch({ lobbyView: false })
    this.persistLobby()
    this.write('room', 'returned to game')
  }

  /** Место в лобби переживает перезагрузку вместе с сессией. */
  private persistLobby(): void {
    const { code, sessionToken, lobbyView } = this.state
    if (code && sessionToken) saveSession({ code, sessionToken, lobby: lobbyView })
  }

  /** Локально вернуться в лобби: снимок, слот и сессия больше не нужны. */
  private resetGame(): void {
    saveSession(null)
    this.patch({
      lobbyView: false,
      snapshot: null,
      role: null,
      playerId: null,
      sessionToken: null,
      code: null,
      lastError: null,
      lastErrorCode: null,
      returning: false,
    })
  }

  /** Смотреть партию, не участвуя. Слот не занимается, токен не выдаётся. */
  watchGame(code: string): void {
    this.patch({ role: 'viewer', lastError: null, lastErrorCode: null })
    this.send({ kind: 'watchGame', code })
  }

  /** Разорвать соединение клиента по id. Проверка переподключения. */
  eject(id: string): void {
    this.send({ kind: 'harness:eject', id })
  }

  /** Удалить комнату из общего списка. Пока в приложении нет ролей админа. */
  deleteRoom(code: string): void {
    this.send({ kind: 'deleteRoom', code })
  }

  /** Представиться стенду. Если соединения ещё нет, уйдёт сразу после него. */
  hello(name: string): void {
    this.pendingName = name
    if (this.socket?.connected) this.send({ kind: 'harness:hello', name })
  }
}

/**
 * Остаток фазы в миллисекундах. Сервер называет срок и своё время, клиент
 * считает сам.
 *
 * Тики держим на клиенте, а не гоним событием с сервера: по проводу нужно
 * одно число на фазу, а не по одному на секунду, и цифра тогда не дёргается
 * от разброса задержек. Расхождение часов снимается тем же снапшотом —
 * `serverNow` приходит вместе со сроком, и поправка пересчитывается при
 * каждом обновлении, в том числе после перезапуска вкладки.
 */
export function useCountdown(endsAt: number | null, serverNow: number | null): number | null {
  const skew = useRef<number | null>(null)
  const [left, setLeft] = useState<number | null>(null)

  /*
   * Разница часов по последнему снапшоту. Никакого обмена ради замера нет:
   * сервер и так кладёт в снапшот своё время рядом со сроком фазы, клиент
   * сверяет его со своим — и всё. В оценку попадает дорога сюда, то есть
   * отсчёт показывает на эту дорогу больше, чем есть; на десятках
   * миллисекунд против секундной цифры это не видно.
   */
  useEffect(() => {
    if (serverNow !== null) skew.current = serverNow - Date.now()
  }, [serverNow])

  useEffect(() => {
    // Срок снят — счёт останавливаем, но последнее число держим: подпись с ним
    // уходит плавно, и на прощание цифра не должна прыгать или исчезать разом.
    if (endsAt === null) return
    // Шаг мельче секунды: иначе видимая цифра меняется с задержкой до секунды.
    const tick = () => setLeft(Math.max(0, endsAt - (Date.now() + (skew.current ?? 0))))
    tick()
    const id = globalThis.setInterval(tick, 200)
    return () => globalThis.clearInterval(id)
  }, [endsAt])

  return left
}

export const bridge = new Bridge()

/** Подписка компонента на состояние моста. Компоненты состояние только читают. */
export function useBridge(): ClientState {
  const [state, setState] = useState<ClientState>(bridge.state)
  useEffect(() => bridge.subscribe(setState), [])
  return state
}
